import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import IconButton from '@material-ui/core/IconButton'
import Popover from '@material-ui/core/Popover'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Radio from '@material-ui/core/Radio'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import LayersIcon from '@material-ui/icons/Layers'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    zIndex: 1000,
    top: ({ top }) => top,
    right: ({ right }) => right,
  },
  btn: {
    width: 36,
    height: 36,
    backgroundColor: '#fff',
    border: '1.5px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    borderRadius: 12,
    color: '#9CA3AF',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#232C57',
      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
      borderColor: 'rgba(35, 44, 87, 0.25)',
    },
  },
  btnActive: {
    backgroundColor: '#232C57',
    color: '#fff',
    borderColor: '#232C57',
    boxShadow: '0 2px 8px rgba(35, 44, 87, 0.4)',
    '&:hover': {
      backgroundColor: '#1a2145',
      color: '#fff',
      borderColor: '#1a2145',
    },
  },
  icon: {
    fontSize: 19,
  },
  popoverContent: {
    padding: theme.spacing(0.5),
    minWidth: 200,
    maxWidth: 280,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.25, 0.75),
    textTransform: 'uppercase',
  },
  groupTitle: {
    fontWeight: 600,
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.25, 0.75),
    marginTop: theme.spacing(0.25),
  },
  layerItem: {
    padding: theme.spacing(0.25, 0.5),
    paddingLeft: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  listItemIcon: {
    minWidth: 28,
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.8rem',
    },
  },
}))

export default function LeafletLayersControl({
  baseLayers = [],
  overlayGroups = [],
  onBaseLayerChange,
  onOverlayToggle,
  top = 76,
  right = 12,
}) {
  const classes = useStyles({ top, right })
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleBaseLayerSelect = (layerName) => {
    if (onBaseLayerChange) {
      onBaseLayerChange(layerName)
    }
  }

  const handleOverlayToggle = (layerName) => {
    if (onOverlayToggle) {
      // Find the overlay across all groups
      let currentLayer = null
      for (const group of overlayGroups) {
        currentLayer = group.overlays.find(o => o.name === layerName)
        if (currentLayer) break
      }
      onOverlayToggle(layerName, !currentLayer?.visible)
    }
  }

  const hasOverlays = overlayGroups.some(group => group.overlays.length > 0)

  const isOpen = Boolean(anchorEl)

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleClick}
        title={t('Layers')}
        className={`${classes.btn} ${isOpen ? classes.btnActive : ''}`}
        size="small"
      >
        <LayersIcon className={classes.icon} />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className={classes.popoverContent}>
          {baseLayers.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                {t('Base Layers')}
              </Typography>
              <List dense disablePadding>
                {baseLayers.map((layer) => (
                  <ListItem
                    key={layer.name}
                    button
                    onClick={() => handleBaseLayerSelect(layer.name)}
                    className={classes.layerItem}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <Radio
                        checked={layer.checked}
                        size="small"
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(layer.name)}
                      className={classes.listItemText}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {baseLayers.length > 0 && hasOverlays && (
            <Divider style={{ margin: '4px 0' }} />
          )}

          {hasOverlays && (
            <>
              <Typography className={classes.sectionTitle}>
                {t('Overlays')}
              </Typography>
              {overlayGroups.map((group, groupIndex) => (
                group.overlays.length > 0 && (
                  <div key={group.name}>
                    <Typography className={classes.groupTitle}>
                      {t(group.name)}
                    </Typography>
                    <List dense disablePadding>
                      {group.overlays.map((overlay) => (
                        <ListItem
                          key={overlay.name}
                          button
                          onClick={() => handleOverlayToggle(overlay.name)}
                          className={classes.layerItem}
                        >
                          <ListItemIcon className={classes.listItemIcon}>
                            <Checkbox
                              checked={overlay.visible}
                              size="small"
                              color="primary"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={t(overlay.name)}
                            className={classes.listItemText}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>
                )
              ))}
            </>
          )}
        </div>
      </Popover>
    </div>
  )
}
